import React, {Fragment, useCallback, useMemo} from 'react';
import {Sheet} from '../../../../components/sheet';
import dayjs from 'dayjs';
import {
  AbsenceInfo,
  getWeekNumber,
  ScheduleCalendarType,
  ShiftWithTasks,
  useScheduleDataContext,
} from '../../../../services';
import {Shift, Absence, AbsenceCaption} from '../../../../components';
import {useScheduleSheetData} from './Hooks';
import {CalendarDateHeader} from './CalendarDateHeader';
import {CourierAvatar, CourierInfo} from './CourierCell';
import {CalendarCellInfo} from './types';
import {Layout, Text} from '@di-nxt/components';
import {t} from '../../../../localization';

type Props = {
  onChange: (nextDate: Date, nextCalendarType: ScheduleCalendarType) => void;
};

export const ScheduleCalendar: React.FC<Props> = ({onChange}) => {
  // Data
  const context = useScheduleDataContext();

  // Transformed sheet model
  const {borderInfo, cellInfos, cellSpanInfos} = useScheduleSheetData(context);

  // Render methods
  const handleRenderCellSpan = useMemo(
    () =>
      cellSpanInfos !== undefined
        ? (_: number, data: ShiftWithTasks | AbsenceInfo) => {
            return 'shift' in data ? (
              <Shift shiftWithTasks={data} kind={context.calendarType} />
            ) : (
              <Absence kind={context.calendarType} absenceInfo={data} />
            );
          }
        : undefined,
    [cellSpanInfos, context],
  );

  const handleRenderCellSpanCaption = useMemo(
    () =>
      cellSpanInfos !== undefined
        ? (_: number, data: ShiftWithTasks | AbsenceInfo) => {
            return 'shift' in data ? (
              <React.Fragment />
            ) : (
              <AbsenceCaption kind={context.calendarType} absenceInfo={data} />
            );
          }
        : undefined,
    [cellSpanInfos, context],
  );

  // Render cell callback
  const handleRenderCell = useCallback(
    (_: number, __: number, info: Readonly<CalendarCellInfo>) => {
      switch (info.row) {
        case '0': // header
          switch (info.col) {
            case '0':
              return <React.Fragment />;
            case '1':
              return context.calendarType !== 'month' ? (
                // HACK: We need to display the week number over
                // column 0 and 1 - but we don't want to draw it
                // as a cellspan because cell spans are hidden when
                // they start on a fixed column
                <span style={{marginLeft: '-48px'}}>
                  <Layout paddingVertical="12" justify="flex-end">
                    <Text variant="inline_16_default" align="end">
                      {context.calendarType !== 'day'
                        ? t('screens.schedule.weekformat', {
                            week: getWeekNumber(context.date),
                          })
                        : dayjs(context.date).format('dddd')}
                    </Text>
                  </Layout>
                </span>
              ) : (
                <Fragment />
              );
            default:
              // Header
              return (
                <CalendarDateHeader
                  calendarType={context.calendarType}
                  date={info.data.date}
                  unassignedTasks={info.data.unassignedTasks}
                  workload={info.data.workload}
                  isEnabled={info.data.enabled}
                  onChange={onChange}
                />
              );
          }
        default:
          switch (info.col) {
            case '0':
              return (
                <CourierAvatar
                  allocation={info.data.workloadHours}
                  member={info.data.member}
                />
              );
            case '1':
              return (
                <CourierInfo
                  allocation={info.data.workloadHours}
                  date={context.date}
                  member={info.data.member}
                />
              );
            default:
              // Render cell content
              return <React.Fragment />;
          }
      }
    },
    [context.calendarType, context.date, onChange],
  );

  return (
    <Sheet<CalendarCellInfo, any>
      firstRowIsHeader
      cellSpanInfos={cellSpanInfos}
      cellInfos={cellInfos}
      cellSpanSpacing={'4'}
      fixedColWidths={[68, 192]}
      fixedColumnIndex={1}
      minRowHeight={64}
      columnBorders={borderInfo}
      onRenderCell={handleRenderCell}
      onRenderCellSpan={handleRenderCellSpan}
      onRenderCellSpanCaption={handleRenderCellSpanCaption}
    />
  );
};
